import { FC, useState, useEffect } from 'react';
import {
    Popover,
    MenuItem,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Tooltip,
    Dialog,
    DialogContent,
    Box,
    Typography,
} from '@mui/material';
import Swal from 'sweetalert2';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { SCUIGeneralDrawer, SCUIReturnProductCopyButton } from '../../../../../atomic/molecules';
import { SCUILoading } from '../../../../../atomic/atoms';
import { SCUIReturnDetailDrawerContent, SCUIReturnDetailModalApprove } from '../../components';
import { useTranslation } from 'react-i18next';

import SCUIGenericDialog from '../../../../../atomic/molecules/SCUIGenericDialog/SCUIGenericDialog';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { RefundQuery } from '../../../../../services/query';
import { fetchReturnDetailRefundImages } from './SCUIReturnDetailProductTable.helper';

export const SCUIReturnDetailProductTable: FC<{
    disableAprroveProduct: any;
    isRejectedReturn: boolean;
    resetForSelectedProduct: any;
    selectable: boolean;
    returnDatasProducts: any;
    returnData: any;
    customerID: any;
    returnReason: any;
    returnRejectedReason: any;
    formattedCreateConfirm: any;
    selectedQrCodes?: any;
    setSelectedQrCodes?: any;
}> = ({
    disableAprroveProduct,
    isRejectedReturn,
    resetForSelectedProduct,
    returnDatasProducts,
    returnData,
    returnReason,
    returnRejectedReason,
    formattedCreateConfirm,
    selectedQrCodes,
    setSelectedQrCodes,
}) => {
    const { t } = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [deletePopoverOpen, setDeletePopoverOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [openModal, setOpenModal] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [product, setProduct] = useState<any>();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState<string | undefined>(undefined);
    const [imageUrls, setImageUrls] = useState<string[]>([]);
    const [isImagesLoading, setIsImagesLoading] = useState<boolean>(true);

    const totalPieces = (returnDatasProducts || []).reduce((sum: any, item: any) => sum + (item.piece || 0), 0);

    const onDrawerOpen = () => setDrawerOpen(true);
    const onDrawerClose = () => setDrawerOpen(false);
    const handleClosePopover = () => {
        setDeletePopoverOpen(false);
        setAnchorEl(null);
    };
    const handleClickProductTable = (e: any, product: any) => {
        console.info(e);
        setProduct(product);
        setDeletePopoverOpen(true);
        setAnchorEl(e.currentTarget);
    };
    const showDetail = () => {
        handleClosePopover();
        onDrawerOpen();
    };
    const approveProduct = () => {
        handleClosePopover();
        onModalOpen();
    };
    const createReturn = (orderNumber: any) => {
        window.open(import.meta.env.VITE_APPURL! + '/createReturn?orderNumber=' + orderNumber, '_blank');
    };
    const onModalOpen = () => setModalOpen(true);
    const onModalClose = () => setModalOpen(false);
    const other_link: any = import.meta.env.VITE_APPURL!;

    const handleImageClick = (imageUrl: any) => {
        setSelectedImage(imageUrl);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };
    const findReasonNameById = (reasonList: any, reasonIdKey: any, reasonId: any) => {
        const foundReason = reasonList && reasonList.find((reason: any) => reason[reasonIdKey] === reasonId);
        return foundReason ? foundReason.reasonName : '';
    };
    const renderReasonDescription = (product: any) => {
        if (product.reason === 15 || product.reason === 9 || product.reason === 3 || product.reason === 44) {
            return (
                <div style={{ color: 'red' }}>
                    <strong>{product.reasonDescription}</strong>
                </div>
            );
        }
        return product.customerReturnReason ?? product.reasonDescription;
    };
    const handleOpenModal = (imageUrl: any) => {
        setSelectedImage(imageUrl);
        setOpenModal(true);
    };
    const handleCloseModal = () => {
        setOpenModal(false);
    };

    useEffect(() => {
        if (returnData?.returnImageUrlList?.length > 0) {
            setIsImagesLoading(true);
            const fetchImages = async () => {
                try {
                    const base64Strings = await fetchReturnDetailRefundImages(
                        returnData?.returnImageUrlList,
                        RefundQuery.GetRefundImages.bind(RefundQuery)
                    );
                    setImageUrls(base64Strings);
                } catch {
                    Swal.fire(t('general.error'), '', 'error');
                } finally {
                    setIsImagesLoading(false);
                }
            };

            fetchImages();
        }
    }, [returnData?.returnImageUrlList]);

    return (
        <>
            <SCUIGenericDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                content={selectedImage}
            ></SCUIGenericDialog>
            <Dialog open={openModal} onClose={handleCloseModal}>
                <DialogContent>
                    <img src={selectedImage} style={{ width: '400px', height: 'auto' }} alt="" />
                </DialogContent>
            </Dialog>
            <Grid
                size={{
                    sm: 12,
                    md: 12,
                    lg: 12,
                    xl: 12,
                }}
            >
                {product && (
                    <SCUIReturnDetailModalApprove
                        disableAprroveProduct={disableAprroveProduct}
                        open={modalOpen}
                        onClose={onModalClose}
                        product={product}
                        returnData={returnData}
                        returnReason={returnReason}
                        selectedQrCodes={selectedQrCodes}
                        setSelectedQrCodes={setSelectedQrCodes}
                        returnRejectedReason={returnRejectedReason}
                        formattedCreateConfirm={formattedCreateConfirm}
                    />
                )}
                <SCUIGeneralDrawer
                    open={drawerOpen}
                    width={'400px'}
                    onClose={(event: any, reason: any) => {
                        if (reason === 'backdropClick') {
                            setDrawerOpen(false);
                        }
                    }}
                >
                    {product && <SCUIReturnDetailDrawerContent product={product} onClose={onDrawerClose} />}
                </SCUIGeneralDrawer>
                <Popover open={deletePopoverOpen} onClose={handleClosePopover} anchorEl={anchorEl}>
                    {returnData.isReturnable && (
                        <MenuItem sx={{ paddingRight: 10 }} onClick={() => resetForSelectedProduct(product.barcode)}>
                            {' '}
                            {t('general.resetThisProduct')}{' '}
                        </MenuItem>
                    )}
                    <MenuItem sx={{ paddingRight: 10 }} onClick={() => showDetail()}>
                        {' '}
                        {t('general.showDetail')}{' '}
                    </MenuItem>
                    {returnData.isReturnable && (
                        <MenuItem onClick={() => approveProduct()}>{t('general.takeAction')}</MenuItem>
                    )}
                    {isRejectedReturn && (
                        <MenuItem onClick={() => createReturn(returnData.orderNumber)}>{'İade Oluştur'}</MenuItem>
                    )}
                </Popover>

                <TableContainer component={Paper} variant="outlined">
                    <Table size="small" aria-label="a dense table">
                        <TableHead>
                            <TableRow sx={{ height: 50 }}>
                                <TableCell style={{ textAlign: 'center' }}>
                                    {' '}
                                    <strong> </strong>{' '}
                                </TableCell>
                                <TableCell style={{ textAlign: 'center' }}>
                                    {' '}
                                    <strong>{t('general.product')}</strong>
                                </TableCell>
                                <TableCell style={{ textAlign: 'center' }} align="right" width={1}>
                                    <strong>{t('general.quantity')}</strong>
                                </TableCell>
                                <TableCell style={{ textAlign: 'center' }} align="right" width={1}>
                                    <strong>{t('general.saleable')}</strong>
                                </TableCell>
                                <TableCell style={{ textAlign: 'center' }} align="right" width={1}>
                                    <strong>{t('general.notSaleable')}</strong>
                                </TableCell>
                                <TableCell style={{ textAlign: 'center' }} align="right" width={1}>
                                    <strong>{t('general.reject')}</strong>
                                </TableCell>
                                <TableCell style={{ textAlign: 'center' }} align="right" width={10}>
                                    <strong>{t('general.returnReason')}</strong>
                                </TableCell>
                                <TableCell style={{ textAlign: 'center' }} align="right" width={10}>
                                    <strong>{t('general.rejectReason')}</strong>
                                </TableCell>
                                <TableCell style={{ textAlign: 'center' }} align="right" width={10}>
                                    <strong>{t('general.operatorSelection')}</strong>
                                </TableCell>
                                <TableCell style={{ textAlign: 'center' }} align="right" width={10}>
                                    <strong>{t('general.status')}</strong>
                                </TableCell>
                                <TableCell style={{ textAlign: 'center' }} align="right" width={50}>
                                    <strong>{t('general.refundMessage')}</strong>
                                </TableCell>
                                <TableCell style={{ textAlign: 'center', minWidth: '150px' }} align="right">
                                    <strong>{t('general.statusMessage')}</strong>
                                </TableCell>
                                <TableCell style={{ textAlign: 'center' }} align="right" width={1}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {returnDatasProducts &&
                                returnReason &&
                                returnRejectedReason &&
                                returnDatasProducts.map((product: any) => (
                                    <>
                                        <TableRow
                                            sx={{
                                                paddingBottom: '3rem',
                                                height: 50,
                                                backgroundColor:
                                                    Number(product.nonRestockQuantity) +
                                                        Number(product.rejectedQuantity) +
                                                        Number(product.restockQuantity) ===
                                                    Number(product.piece)
                                                        ? '#75c693'
                                                        : '',
                                            }}
                                        >
                                            <TableCell
                                                align="right"
                                                style={{ textAlign: 'left', margin: 0, padding: 0 }}
                                                width={100}
                                            >
                                                <div
                                                    style={{
                                                        position: 'relative',
                                                        display: 'inline-block',
                                                    }}
                                                >
                                                    <img
                                                        src={product.productImageUrl}
                                                        style={{ width: '110px', height: 'auto' }}
                                                        alt=""
                                                    />
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            right: 0,
                                                            cursor: 'pointer',
                                                            color: '#5b99f3',
                                                        }}
                                                        onClick={() => handleOpenModal(product.productImageUrl)}
                                                    >
                                                        <ZoomInIcon style={{ height: 'auto', width: '20px' }} />
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                style={{ textAlign: 'center', position: 'relative' }}
                                                width={100}
                                            >
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        right: 4,
                                                    }}
                                                >
                                                    <SCUIReturnProductCopyButton
                                                        product={product}
                                                        imageUrl={product.productImageUrl}
                                                        showLabel={false}
                                                        iconSize="small"
                                                    />
                                                </Box>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <p>
                                                        {product.brandId === 255
                                                            ? product.newBrandName
                                                            : product.brandName}
                                                    </p>
                                                    <p>{product.modelTitle}</p>
                                                    {product.numberOfComponentName && (
                                                        <div style={{ textAlign: 'center' }}>
                                                            <p>{product.numberOfComponentName}</p>
                                                        </div>
                                                    )}
                                                    <p>
                                                        <b>{product.barcode}</b>
                                                    </p>
                                                    <div className="linktxt">
                                                        <a
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            href={other_link + '/productDetail/' + product?.optionId}
                                                        >
                                                            <b>
                                                                {' '}
                                                                {product.ozelKod1} - {product.renkKod}
                                                            </b>
                                                        </a>
                                                    </div>
                                                    <p>{product.size}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                style={{ textAlign: 'center' }}
                                                component="th"
                                                scope="row"
                                                width={10}
                                            >
                                                {' '}
                                                {product.piece}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                style={{ textAlign: 'center' }}
                                                component="th"
                                                scope="row"
                                                width={10}
                                            >
                                                {' '}
                                                {product.restockQuantity}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                style={{ textAlign: 'center' }}
                                                component="th"
                                                scope="row"
                                                width={10}
                                            >
                                                {' '}
                                                {product.nonRestockQuantity}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                style={{ textAlign: 'center' }}
                                                component="th"
                                                scope="row"
                                                width={10}
                                            >
                                                <div style={{ color: 'red' }}>
                                                    <strong> {product.rejectedQuantity}</strong>
                                                </div>
                                            </TableCell>
                                            <TableCell align="right" style={{ textAlign: 'center' }} width={20}>
                                                {' '}
                                                {renderReasonDescription(product)}
                                            </TableCell>
                                            {returnRejectedReason && (
                                                <TableCell align="right" style={{ textAlign: 'center' }} width={20}>
                                                    {' '}
                                                    <div style={{ color: 'red' }}>
                                                        <strong>
                                                            {product.rejectedReasonDescription ||
                                                                findReasonNameById(
                                                                    returnRejectedReason,
                                                                    'reasonId',
                                                                    product.rejectedReason
                                                                )}
                                                        </strong>
                                                    </div>
                                                </TableCell>
                                            )}
                                            <TableCell align="right" style={{ textAlign: 'center' }} width={10}>
                                                {' '}
                                                {product.operatorReturnReasonDescription ||
                                                    findReasonNameById(
                                                        returnReason,
                                                        'returnReasonId',
                                                        product.operatorReturnReasonId
                                                    )}
                                            </TableCell>
                                            <TableCell align="right" style={{ textAlign: 'center' }} width={10}>
                                                {product.returnItemStatusKey}
                                            </TableCell>
                                            <TableCell align="right" style={{ textAlign: 'center' }} width={50}>
                                                <Chip
                                                    color={product.isReturnable ? 'success' : 'error'}
                                                    label={
                                                        product.isReturnable
                                                            ? t('refund.refundable')
                                                            : t('refund.nonRefundable')
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell align="right" style={{ textAlign: 'center' }} width={100}>
                                                <Tooltip
                                                    title={
                                                        <h1>
                                                            {product.returnMessages ? product.returnMessages.replace('/n', '').replace('/r', '') : ''}
                                                        </h1>
                                                    }
                                                >
                                                    <span style={{ color: 'red' }}>
                                                        <strong>
                                                            {product.returnMessages ? product.returnMessages.replace('/n', '').replace('/r', '') : ''}
                                                        </strong>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell align="right" style={{ textAlign: 'center' }} width={1}>
                                                <TableCell align="right" style={{ textAlign: 'center' }} width={1}>
                                                    <MoreVertIcon
                                                        className="svg_icons"
                                                        onClick={(e) => handleClickProductTable(e, product)}
                                                    />
                                                </TableCell>
                                            </TableCell>
                                        </TableRow>
                                    </>
                                ))}
                        </TableBody>
                    </Table>
                    <Box sx={{ display: 'flex', margin: '16px 8px', padding: '8px' }}>
                        <Typography sx={{ fontWeight: 'bold', fontSize: '18px', marginLeft: 'auto' }}>{`${t(
                            'return.totalPieces'
                        )}: ${totalPieces}`}</Typography>
                    </Box>
                    {returnData.returnImageUrlList && returnData.returnImageUrlList.length > 0 && (
                        <div
                            style={{
                                padding: '1rem',
                                margin: '1rem',
                                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <h5
                                style={{
                                    fontWeight: 'bold',
                                    fontStyle: 'italic',
                                    color: 'red',
                                    padding: '1rem',
                                }}
                            >
                                İade Fotoğrafları
                            </h5>
                            <TableRow style={{ marginTop: '5rem' }}>
                                <TableCell colSpan={12}>
                                    <Grid container spacing={2}>
                                        {isImagesLoading ? (
                                            <SCUILoading />
                                        ) : (
                                            (imageUrls || []).map((photo, index) => (
                                                <Grid
                                                    key={index}
                                                    size={{
                                                        xs: 3,
                                                        sm: 2,
                                                        md: 1,
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
                                                            borderRadius: '4px',
                                                            overflow: 'hidden',
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        <img
                                                            src={photo}
                                                            style={{ width: '100%', height: 'auto' }}
                                                            onClick={() => handleImageClick(photo)}
                                                            alt=""
                                                        />
                                                    </div>
                                                </Grid>
                                            ))
                                        )}
                                    </Grid>
                                </TableCell>
                            </TableRow>
                        </div>
                    )}
                </TableContainer>
            </Grid>
        </>
    );
};
